/* eslint-disable no-unused-vars */
// frontend/user/src/components/Header.js

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../config/firebase";
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTour } from "../contexts/TourContext"; // Importamos el hook del tour

// Hook personalizado para obtener datos del usuario de Firestore
function useUserDB(user) {
  const [userDB, setUserDB] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserDB = async () => {
      if (user && user.uid) {
        setLoading(true);
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setUserDB(userSnap.data());
          } else {
            setUserDB(null);
          }
        } catch (error) {
          console.error(
            "Error al obtener datos de usuario de Firestore:",
            error
          );
          setUserDB(null);
        } finally {
          setLoading(false);
        }
      } else {
        setUserDB(null);
        setLoading(false);
      }
    };
    
    // Solo hacer la consulta si el usuario está autenticado
    if (user && user.uid) {
      fetchUserDB();
    }
  }, [user]); // Incluir user completo como dependencia para satisfacer ESLint

  return { userDB, loading };
}

function Header() {
  const { user, logout } = useAuth();
  const { startTour } = useTour(); // Obtenemos la función para iniciar el tour desde el contexto
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Solo cerrar si el clic NO es en el dropdown
      if (isDropdownOpen && !event.target.closest('.dropdown-content')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  const handleHowItWorksClick = (e) => {
    e.preventDefault(); // Prevenimos el comportamiento por defecto del enlace
    startTour(); // Llamamos a la función global para iniciar el tour
    setIsMobileMenuOpen(false); // Cerrar menú móvil si está abierto
  };

  // Si hay un usuario logueado, traemos sus datos de Firestore y los guardamos en userDB
  // Usamos el hook personalizado en el componente Header
  const { userDB, loading } = useUserDB(user);
  ////console.log(userDB);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      {/* AVISO de email no verificado */}
      {user && !user.emailVerified && (
        <div className="w-full bg-yellow-100 text-yellow-800 text-center py-2 px-4 text-sm font-semibold">
          Tu email aún no está verificado. Algunas funciones estarán limitadas
          hasta que completes la verificación.{" "}
          <a
            href="/email-verification"
            className="underline text-yellow-900 ml-2"
          >
            Verificar ahora
          </a>
        </div>
      )}
      
      <div className="max-w-7xl w-full mx-auto flex items-center justify-between py-2 px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" className="flex-shrink-0">
            <img
              src="/images/logo-3v0.png"
              alt="Alquilalo"
              className="h-8 sm:h-10 md:h-12 w-auto max-w-[200px] sm:max-w-[250px] md:max-w-[300px]"
            />
          </Link>
        </div>

        {/* Navegación Desktop */}
        <nav className="hidden md:flex gap-6 lg:gap-8 text-gray-700 font-medium text-base lg:text-lg">
          <Link to="/items" className="hover:text-[#009688] transition-colors">
            En Alquiler
          </Link>

          <button
            onClick={handleHowItWorksClick}
            className="hover:text-[#009688] text-gray-700 font-medium text-base lg:text-lg transition-colors"
          >
            Cómo Funciona
          </button>
          <Link to="/ayuda" className="hover:text-[#009688] transition-colors">
            Centro de Ayuda
          </Link>
          <Link to="/contacto" className="hover:text-[#009688] transition-colors">
            Contacto
          </Link>
        </nav>

        {/* Botones de acción Desktop */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6">
          {user ? (
            <>
              <Link
                to="/publicar"
                className="bg-[#E6AC00] text-gray-900 font-semibold px-4 lg:px-6 py-2 rounded-full text-sm lg:text-lg shadow hover:bg-[#d9a300] transition-colors min-h-[44px] inline-flex items-center justify-center"
              >
                Publicar
              </Link>
              <div className="relative user-dropdown-container">
                <button 
                  type="button"
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 min-h-[44px] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Menú de cuenta de usuario"
                >
                  <img
                    src={user.photoURL || "/images/user_icon.png"}
                    alt="Usuario"
                    className="h-8 w-8 lg:h-10 lg:w-10 rounded-full border-2 border-[#FFC107]"
                  />
                  <span className="text-gray-700 text-sm lg:text-base hidden lg:block">
                    {loading ? (
                      <span className="text-gray-500">Cargando...</span>
                    ) : (
                      userDB?.name || user?.displayName || "Usuario"
                    )}
                  </span>
                  {/* Flecha del dropdown */}
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                
                {/* Dropdown usando Portal para evitar conflictos de CSS */}
                {isDropdownOpen && createPortal(
                  <div 
                    className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 py-3 z-[9999] dropdown-content"
                    style={{
                      top: '80px',
                      right: '20px',
                      width: '256px',
                      overflow: 'visible'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <div className="text-sm font-semibold text-gray-900">
                        {loading ? (
                          <span className="text-gray-500">Cargando...</span>
                        ) : (
                          userDB?.name || user?.displayName || "Usuario"
                        )}
                      </div>
                      <div className="text-sm text-gray-500 truncate mt-1">
                        {user.email}
                      </div>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/perfil"
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-150 ease-in-out"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Mi Perfil
                      </Link>
                      <Link
                        to="/cuenta"
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-150 ease-in-out"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826-3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Mi Cuenta
                      </Link>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          logout();
                        }}
                        className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-150 ease-in-out"
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Cerrar Sesión
                      </button>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-gray-700 hover:text-[#009688] font-medium text-sm lg:text-base transition-colors"
              >
                Iniciar Sesión
              </Link>
              <Link
                id="login-button"
                to="/register"
                className="bg-[#E6AC00] text-gray-900 font-semibold px-4 lg:px-6 py-2 rounded-full text-sm lg:text-lg shadow hover:bg-[#d9a300] transition-colors min-h-[44px] inline-flex items-center justify-center"
              >
                Registrarse
              </Link>
            </>
          )}
        </div>

        {/* Botón menú móvil */}
        <div className="md:hidden flex items-center gap-2">
          {user && (
            <Link
              to="/publicar"
              className="bg-[#E6AC00] text-gray-900 font-semibold px-3 py-2 rounded-full text-sm shadow hover:bg-[#d9a300] transition-colors min-h-[44px] inline-flex items-center justify-center"
            >
              Publicar
            </Link>
          )}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-3 min-w-[44px] min-h-[44px] rounded-md text-gray-800 hover:text-[#009688] hover:bg-gray-100 transition-colors flex items-center justify-center"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-menu"
            aria-label={isMobileMenuOpen ? "Cerrar menú de navegación" : "Abrir menú de navegación"}
          >
            <svg
              className="h-6 w-6 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Menú móvil */}
      {isMobileMenuOpen && (
        <div id="mobile-navigation-menu" className="md:hidden bg-white border-t border-gray-200" role="navigation" aria-label="Navegación móvil">
          <div className="px-4 py-2 space-y-1">
            {/* Navegación móvil */}
            <Link
              to="/items"
              className="block px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              En Alquiler
            </Link>
            <button
              onClick={handleHowItWorksClick}
              className="block w-full text-left px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
            >
              Cómo Funciona
            </button>
            <Link
              to="/ayuda"
              className="block px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Centro de Ayuda
            </Link>
            <Link
              to="/contacto"
              className="block px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contacto
            </Link>
            
            {/* Separador */}
            <div className="border-t border-gray-200 my-2"></div>
            
            {/* Usuario móvil */}
            {user ? (
              <div className="px-3 py-2">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={user.photoURL || "/images/user_icon.png"}
                    alt="Usuario"
                    className="h-10 w-10 rounded-full border-2 border-[#FFC107]"
                  />
                  <div>
                    <div className="text-gray-900 font-medium">
                      {loading ? (
                        <span className="text-gray-500">Cargando...</span>
                      ) : (
                        userDB?.name || user?.displayName || "Usuario"
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </div>
                <div className="space-y-1">
                  <Link
                    to="/perfil"
                    className="block px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Mi Perfil
                  </Link>
                  <Link
                    to="/cuenta"
                    className="block px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Mi Cuenta
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-gray-700 hover:text-[#009688] hover:bg-gray-50 rounded-md transition-colors"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 px-3 py-2">
                <Link
                  to="/login"
                  className="block w-full text-center bg-gray-100 text-gray-700 font-medium px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Iniciar Sesión
                </Link>
                <Link
                  to="/register"
                  className="block w-full text-center bg-[#E6AC00] text-gray-900 font-semibold px-4 py-3 min-h-[48px] rounded-md hover:bg-[#d9a300] transition-colors flex items-center justify-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Registrarse
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
